import React, { useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Space, Form, Input, Button, Modal } from 'antd';

import { ApplicationUIContext, InputPhone, inputPhoneAntdFormValidator } from '@yuhuinc/ui-library';

import { findDuplicates } from '../store/actions';
import BirthdayPicker, { birthdayPickerMinimumValueValidator } from './BirthdayPicker';

import { getTranslatedString } from 'helpers/i18n';
import { convertPhoneNumberObjectToString } from 'ui-library/components/InputPhone/InputPhoneHelpers';

const StyledSpace = styled(Space)`
  width: 100%;

  .ant-picker {
    width: 100%;
  }
`;

const validationRules = [
  {
    required: true,
    message: getTranslatedString(
      'landlordPortal.applicationManagement.inviteLeadToApply.requiredField'
    ),
    validateTrigger: 'onSubmit',
  },
];

const birthdayValidationRules = [
  {
    required: true,
    message: getTranslatedString(
      'landlordPortal.applicationManagement.inviteLeadToApply.requiredField'
    ),
    validateTrigger: 'onSubmit',
    transform(value) {
      if (value?.format) {
        return value.format('YYYY-MM-DD');
      }

      return value;
    },
  },
  {
    validator: birthdayPickerMinimumValueValidator,
    warningOnly: true,
    validateTrigger: 'onChange',
  },
];

const inputPhoneValidationRules = [
  {
    required: true,
    message: getTranslatedString(
      'landlordPortal.applicationManagement.inviteLeadToApply.requiredField'
    ),
    validateTrigger: 'onSubmit',
    transform(value) {
      if (value) {
        return convertPhoneNumberObjectToString(value);
      }

      return value;
    },
  },
  {
    validator: inputPhoneAntdFormValidator,
    validateTrigger: ['onSubmit', 'onChange'],
  },
];

function NewOrEditApplicant({
  initialValues = {},
  extraFormItems = null,
  onSubmit,
  onCancel,
  onError,
}) {
  const { isMobile } = useContext(ApplicationUIContext);
  const additionalInformationForm = Form.useFormInstance();
  const applicationSelectedApplicant = additionalInformationForm
    ? additionalInformationForm.getFieldValue('selectedApplicant')
    : initialValues;
  const duplicateLead = useRef();
  const [form] = Form.useForm();

  function onSelectDuplicate() {
    onSubmit(duplicateLead.current);
  }

  function onFinish(applicant) {
    onSubmit({
      ...applicant,
      full_name: `${applicant.first_name} ${applicant.last_name}`,
      phone_number: convertPhoneNumberObjectToString(applicant.phone_number),
    });
  }

  function onFinishFailed({ errorFields }) {
    const duplicateEmailError = errorFields.find(
      (errorField) =>
        errorField.name.includes('email') &&
        errorField.errors.includes(
          getTranslatedString(
            'landlordPortal.applicationManagement.inviteLeadToApply.duplicateEmail'
          )
        )
    );

    if (duplicateEmailError && duplicateLead.current) {
      return onError({
        message: getTranslatedString(
          'landlordPortal.applicationManagement.inviteLeadToApply.duplicateEmail.message'
        ),
        description: (
          <>
            <p>
              {getTranslatedString('landlordPortal.applicationManagement.inviteLeadToApply.user')}:{' '}
              {duplicateLead.current.name}
              <br />
              {(duplicateLead.current.cotenants || []).map((cotenant) => (
                <span key={cotenant.id} style={{ display: 'block' }}>
                  {getTranslatedString(
                    `landlordPortal.applicationManagement.inviteLeadToApply.${cotenant.cotenant_type}`
                  )}
                  : {cotenant.property_name} unit {cotenant.unit_number}
                </span>
              ))}
            </p>
            <Button type="link" size={isMobile && 'large'} onClick={onSelectDuplicate}>
              {getTranslatedString(
                'landlordPortal.applicationManagement.inviteLeadToApply.duplicateEmail.selectThisContact'
              )}
            </Button>
          </>
        ),
      });
    }

    if (errorFields.length > 1) {
      return onError({
        description: getTranslatedString(
          'landlordPortal.applicationManagement.inviteLeadToApply.errorsOnThisPage'
        ),
      });
    }
  }

  function onClickCancel() {
    if (form.isFieldsTouched()) {
      Modal.warning({
        title: getTranslatedString(
          'landlordPortal.applicationManagement.newApplicant.unsavedChangesModal.header'
        ),
        onOk: () => {
          onCancel();
        },
        okText: getTranslatedString('okay'),
        okCancel: true,
        cancelText: getTranslatedString('cancel'),
        content: getTranslatedString(
          'landlordPortal.applicationManagement.newApplicant.unsavedChangesModal.body'
        ),
        destroyOnClose: true,
      });
    } else {
      onCancel();
    }
  }

  return (
    <Form
      layout="vertical"
      size={isMobile && 'large'}
      form={form}
      scrollToFirstError
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={initialValues}
    >
      <Form.Item name="applicant_type" hidden />
      <Form.Item name="id" hidden />
      <StyledSpace direction="vertical" size="middle">
        <Form.Item
          name="email"
          rules={[
            ...validationRules,
            {
              async validator(_, value) {
                if (!value) {
                  return Promise.resolve();
                }

                // When to skip validation:
                // When the user is editing an existing applicant and the email hasn't changed
                // When it's a new applicant and the form hasn't been submitted yet (InviteLeadToApply)
                if (initialValues.email) {
                  return Promise.resolve();
                }

                if (
                  applicationSelectedApplicant &&
                  applicationSelectedApplicant.email?.toLowerCase() === value.toLowerCase()
                ) {
                  duplicateLead.current = null;

                  return Promise.reject(
                    new Error(
                      getTranslatedString(
                        'landlordPortal.applicationManagement.inviteLeadToApply.duplicateEmail'
                      )
                    )
                  );
                }

                const foundDuplicate = await findDuplicates(value, initialValues.id);

                if (!foundDuplicate) {
                  return Promise.resolve();
                }

                duplicateLead.current = foundDuplicate.lead;

                return Promise.reject(
                  new Error(
                    getTranslatedString(
                      'landlordPortal.applicationManagement.inviteLeadToApply.duplicateEmail'
                    )
                  )
                );
              },
              validateTrigger: 'onSubmit',
            },
          ]}
          label={getTranslatedString(
            'landlordPortal.applicationManagement.inviteLeadToApply.email'
          )}
          hasFeedback
        >
          <Input type="email" />
        </Form.Item>
        <Form.Item
          name="first_name"
          rules={validationRules}
          label={getTranslatedString(
            'landlordPortal.applicationManagement.inviteLeadToApply.firstName'
          )}
          hasFeedback
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="last_name"
          rules={validationRules}
          label={getTranslatedString(
            'landlordPortal.applicationManagement.inviteLeadToApply.lastName'
          )}
          hasFeedback
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="birthday"
          rules={birthdayValidationRules}
          label={getTranslatedString(
            'landlordPortal.applicationManagement.inviteLeadToApply.dateOfBirth'
          )}
          hasFeedback
        >
          <BirthdayPicker placeholder={false} />
        </Form.Item>
        <Form.Item
          name="phone_number"
          rules={inputPhoneValidationRules}
          label={getTranslatedString(
            'landlordPortal.applicationManagement.inviteLeadToApply.phoneNumber'
          )}
          hasFeedback
        >
          <InputPhone />
        </Form.Item>
        {extraFormItems}
        <Space>
          <Button type="primary" htmlType="submit">
            {initialValues.email
              ? getTranslatedString('landlordPortal.applicationManagement.inviteLeadToApply.save')
              : getTranslatedString('landlordPortal.applicationManagement.inviteLeadToApply.add')}
          </Button>
          <Button htmlType="button" onClick={onClickCancel}>
            {getTranslatedString('landlordPortal.applicationManagement.inviteLeadToApply.cancel')}
          </Button>
        </Space>
      </StyledSpace>
    </Form>
  );
}

NewOrEditApplicant.propTypes = {
  initialValues: PropTypes.any,
  extraFormItems: PropTypes.any,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  onError: PropTypes.func,
};

export default NewOrEditApplicant;
